import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { AppModules } from '@globals/types/AppModules';
import { HelloResponse } from '@globals/types/Hello';
import getDeviceType from '@globals/utilities/GetDeviceType';

/**
 * Flaga modyfikująca działanie aplikacji
 */
type flagType = ''
  | 'buyerViewNew'
  | 'showBuyerInAppReview'
  | 'branchIoQrLoginOnlyForOferteoEmail'
  | 'buyerPasswordSet'
  | 'buyerPhoneConfirmed'
  | 'showGoogleLogin'
  | 'autentiHideTrustedProfile'
  | 'autentiHideID'
  | 'autentiHideMyID'
  | 'the_best_award_season'
  | 'ap_promotional_campaign_enabled'
  | 'displayAutentiProfileMessage'
  | 'hideUncoverCounter'
  | 'accountRechargeRedesign'
  | 'showExpressInvitationBanner'
  | 'newMessagesSinceLastVisit'
  | 'enableAppStorePayments'
  | 'hasPromoPacket'
  | 'blackWeek'
  | 'debug'
  | 'hasMobileApp'
  | 'buyerLoadRfpDetailsNewWay'
  | 'showBannerTheBest'
  | 'displayTheBestSocialMediaScreen' // Ekran Najlepsi z możliwością pobrania plików na SM
  | 'reviewCustomersContestEnabled' // Konkurs na pozyskiwanie opinii 2025
  | 'phoneVerified'
  | 'inExtraMatching' // Czy jest dodany do ExtraMatchingu
  | 'isDownloadMobileAppTopBarDismissed' // belka do apki na BV
  | 'abTestBuyerviewContactCustomersBox' // A/B test do odkrywania numeru telefonu na BV
  | 'isDownloadMobileAppTopBarDismissed' // belka do apki na BV
  | 'uncoversBlocked' // Czy odkrycia są zablokowane z powodu niezapłaconej faktury
  | 'enableNewCustomerCardInRfpDetailsBV' // Czy włączyć nowy wygląd kafelka customera w podglądzie RFP na BV
  | 'newRfpAiValuationView' // Nowy widok wyceny AI w RFP na BV
  | 'rfpTileLabelsTest' // Test nowych labelek na kafelku / podglądzie RFP (POS-1914)
;

export const useAppStore = defineStore('app', () => {
	const state = {
		module: ref(''),
		sessionId: ref(''),
		flags: ref<string[]>([]),
		baseUrl: ref(document.location.hostname), // Domain
		websocketUrl: ref(''), // WS
		googleTrackingId: ref('UA-2440395-3'), // UA
		googleAnalyticsData: ref({ userType: 'none' }), // Gtm
		fbTrackingId: ref(''), // Facebook
		userComTrackingId: ref(''), // User.com
		isVirtualKeyboardVisible: ref(false),
		isInApp: ref(false),
		pathname: ref(''),
		nextRouteNameAttempt: ref(''),
		isScreenDimmed: ref(false),
		isSavingScreenVisible: ref(true),
		windowWidth: ref(document.body.clientWidth),
		googleReviewsData: ref<{
			reviews: number;
			placeUrl: string;
			idGoogle: string | null;
			tsModified: number;
			rating: number;
			id: number;
			enabled: boolean;
			idCustomer: number;
		}>({
			reviews: 0,
			placeUrl: '',
			idGoogle: null,
			tsModified: 0,
			rating: 0,
			id: 0,
			enabled: false,
			idCustomer: 0,
		}),
	};

	const actions = {
		update(rsvp: HelloResponse) {
			this.addResizeListener();

			state.sessionId.value = rsvp.sessionId || '';
			state.baseUrl.value = rsvp.baseUrl || document.location.hostname;
			state.websocketUrl.value = rsvp.baseWebsocketUrl || '';
			state.googleTrackingId.value = rsvp.ua || 'UA-2440395-3';
			state.googleAnalyticsData.value = rsvp.analytics || { userType: 'none' };
			state.fbTrackingId.value = rsvp.facebook || '1439591673009945';
			state.userComTrackingId.value = rsvp.userComApiKey || '';

			actions.updateFlags(rsvp);
			state.isInApp.value = typeof wrapee !== 'undefined';

			state.googleReviewsData.value = rsvp.markGoogle || {
				reviews: 0,
				placeUrl: '',
				idGoogle: null,
				tsModified: 0,
				rating: 0,
				id: 0,
				enabled: false,
				idCustomer: 0,
			};
		},
		updateFlags(rsvp: HelloResponse) {
			const { user } = rsvp;
			state.flags.value = rsvp.flags || [];

			// Dodaj flagi do debugu z localStorage
			try {
				const customFlags = window.localStorage.getItem('flags');
				if (customFlags) {
					state.flags.value.push(...customFlags.split(',') || []);
				}
			} catch (err) {
				window.localStorage.setItem('flags', '');
			}

			if (rsvp.frontEndDebug) {
				rsvp.flags.push('frontEndDebug');
			}

			// CustomerApp
			if (user && user.idCustomer) {
				// Najlepsi 2023
				const awardsDeadline = window.localStorage['awardsDeadline_2023'] || '2023-05-11';
				window.localStorage['awardsDeadline_2023'] = awardsDeadline;
				if (Number(new Date()) < Number(new Date(awardsDeadline))) {
					state.flags.value.push('the_best_award_season');
				}

				// Czy pierwszą stroną mają być Kontakty, czy zapytania?
				if (user.newMessagesSinceLastVisit) {
					state.flags.value.push('newMessagesSinceLastVisit');
				}
			}

			// BuyerApp
			if (user) {
				if (user.passwordSet) {
					state.flags.value.push('buyerPasswordSet');
				}

				if (user.buyerViewNew) {
					state.flags.value.push('buyerViewNew');
				}

				if (user.phoneConfirmed) {
					state.flags.value.push('buyerPhoneConfirmed');
				}

				if (window.localStorage.getItem('isDownloadMobileAppTopBarDismissed')) {
					state.flags.value.push('isDownloadMobileAppTopBarDismissed');
				}
			}
		},
		hasFlag(flag: flagType) {
			return state.flags.value.indexOf(flag) !== -1;
		},
		addFlag(flag: flagType) {
			if (!this.hasFlag(flag)) {
				state.flags.value.push(flag);
			}
		},
		removeFlag(flag: flagType) {
			const flagIndex = state.flags.value.findIndex((f: string) => f === flag);
			if (flagIndex >= 0) { state.flags.value.splice(flagIndex, 1); }
		},

		addResizeListener() {
			window.addEventListener('resize', () => {
				state.windowWidth.value = document.body.clientWidth;
			});
		},

		/**
		 * Wysyła informację analityczną zaraz po wejściu na stronę.
		 * Zawiera ona informację o typie urządzenia, status pozwolenia
		 * na powiadomienia Push oraz token.
		 */
		sendStartupAnalytics() {
			window.setTimeout(() => {
				const { userAgent } = navigator;
				const isApp = window.localStorage['IsApp'] === 'yes' || userAgent.indexOf('appstore.oferteo') > -1;

				let pushPermission;
				if (isApp) {
					pushPermission = 'granted';
				} else if (typeof Notification !== 'function') {
					pushPermission = 'denied';
				} else {
					pushPermission = Notification.permission;
				}

				if (navigator.userAgent.indexOf('appstore.oferteo') > -1) {
					pushPermission = window.localStorage.CordovaEnableTracking === 'yes' ? 'granted' : 'denied';
				}

				fetch(`/profile/analytics-set-device-type.ofer?deviceType=${getDeviceType()}&pushPermission=${pushPermission}&token=${window.localStorage['notificationToken']}`);
			}, 5000);
		},

		/**
		 * Procedury wykonywane tylko dla aplikacji mobilnych.
		 */
		mobileAppRoutine() {
			const { userAgent: ua } = navigator;
			const isAppleApp = ua.indexOf('appstore.oferteo') > -1;
			const isApplePhone = ua.match(/iPhone/i);

			// Wyłącz pokazywanie się paska z prośbą o instalację PWA.
			window.addEventListener('beforeinstallprompt', (e) => {
				e.preventDefault();
			});

			// Zapisz w localStorage, że jesteśmy w aplikacji iOS
			if (isAppleApp) {
				window.localStorage['IsApp'] = true;
			}

			// Zapisz w localStorage, jeśli przyszliśmy z TWA
			if (document.referrer.includes('android-app://')) {
				window.localStorage['IsApp'] = 'yes';
			}

			// Wyłącz przybliżenie ekranu po kliknięciu w input.
			if (isAppleApp || isApplePhone) {
				const viewportElement = document.querySelector('meta[name="viewport"]');
				viewportElement?.setAttribute(
					'content',
					'width=device-width, initial-scale=1, maximum-scale=1',
				);
			}
		},
	};

	const getters = {
		isAdminModule: computed(() => state.module.value === AppModules.ADMIN),
		isSaving: computed({
			get() {
				return state.isSavingScreenVisible.value;
			},
			set(newValue) {
				if (newValue) {
					state.isSavingScreenVisible.value = true;
				} else {
					window.setTimeout(() => {
						state.isSavingScreenVisible.value = false;
					}, 150);
				}
			},
		}),
	};

	return { ...state, ...actions, ...getters };
});
